import React from "react";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
} from "recharts";
import { SizeMe } from "react-sizeme";

const RADIAN = Math.PI / 180;
/** Label on slice: show name + % only when segment is large enough to avoid clutter */
const renderSliceLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  name,
}: {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
  name: string;
}) => {
  if (percent < 0.05) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#000"
      textAnchor="middle"
      dominantBaseline="central"
      style={{ fontSize: 11, fontWeight: 600 }}
    >
      {percent >= 0.1 ? `${name} ${(percent * 100).toFixed(0)}%` : `${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

type Props = { data: { name: string; value: number; color: string }[] };

export const CategoriesPieChart: React.FC<Props> = ({ data }) => {
  const chartData = React.useMemo(
    () =>
      [...data]
        .map((x) => ({ ...x, value: Math.abs(Number(x.value)) }))
        .sort((a, b) => b.value - a.value),
    [data]
  );
  const total = chartData.reduce((s, d) => s + d.value, 0);

  return (
    <SizeMe>
      {({ size }) => {
        const width = size.width || 300;
        const height = 240;
        return (
          <div style={{ width: "100%", height }}>
            <RechartsPieChart width={width} height={height}>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={70}
                dataKey="value"
                nameKey="name"
                label={renderSliceLabel}
                labelLine={false}
              >
                {chartData.map((entry, index) => (
                  <Cell key={entry.name} fill={entry.color ?? `hsl(${(index * 137) % 360}, 65%, 55%)`} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string) => [
                  `$${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${total > 0 ? ((value / total) * 100).toFixed(1) : 0}%)`,
                  name,
                ]}
                contentStyle={{ fontSize: 12 }}
              />
            </RechartsPieChart>
          </div>
        );
      }}
    </SizeMe>
  );
};

export default CategoriesPieChart;
